<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useImportCSV } from '@/composables/useSectorData';
import { ImportResult } from '@/types/sector';

const emit = defineEmits<{
  close: [];
  done: [msg: string];
  error: [msg: string];
}>();

const fileInput = ref<HTMLInputElement | null>(null);
const dialogRef = ref<HTMLDivElement | null>(null);
const result = ref<ImportResult | null>(null);

const mutation = useImportCSV();

async function startImport() {
  const file = fileInput.value?.files?.[0];
  if (!file) {
    emit('error', '请选择 CSV 文件');
    return;
  }
  try {
    result.value = await mutation.mutateAsync(file);
    if (result.value.successCount > 0) {
      emit('done', `成功导入 ${result.value.successCount} 条数据`);
    }
  } catch (e) {
    emit('error', (e as Error).message ?? '导入失败');
  }
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') emit('close');
}

onMounted(() => {
  document.addEventListener('keydown', onKeydown);
  document.body.style.overflow = 'hidden';
});
onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown);
  document.body.style.overflow = '';
});
</script>

<template>
  <div class="fixed inset-0 z-40 flex items-center justify-center p-4 animate-fade-in" @click.self="emit('close')">
    <div class="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>
    <div
      ref="dialogRef"
      class="card relative w-full max-w-lg animate-slide-up shadow-elevated"
      role="dialog"
      aria-modal="true"
      aria-labelledby="import-title"
    >
      <div class="mb-4 flex items-center justify-between">
        <h2 id="import-title" class="text-base font-semibold">📥 导入 CSV 数据</h2>
        <button class="btn btn-ghost px-2 py-1 text-xs" @click="emit('close')" aria-label="关闭">✕</button>
      </div>

      <div class="mb-3 rounded-lg p-3 text-xs" style="background: rgb(var(--color-surface-muted)); color: rgb(var(--color-ink-muted));">
        <p class="mb-1 font-medium">CSV 格式要求：</p>
        <ul class="ml-4 list-disc space-y-0.5">
          <li>表头：<code>日期,第一名,第二名,第三名</code></li>
          <li>日期：<code>YYYY-MM-DD</code></li>
          <li>已存在日期将被跳过</li>
        </ul>
      </div>

      <input ref="fileInput" type="file" accept=".csv,text/csv" class="input" />

      <div v-if="mutation.isPending.value" class="mt-3 flex items-center gap-2 text-sm text-ink-muted">
        <div class="h-3 w-3 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
        正在导入...
      </div>

      <div v-if="result" class="mt-3 space-y-2 text-sm">
        <p v-if="result.successCount > 0" class="text-success">✓ 成功导入 {{ result.successCount }} 条</p>
        <p v-if="result.duplicates.length" class="text-warning">⚠ {{ result.duplicates.length }} 条重复已跳过</p>
        <details v-if="result.errors.length" class="text-danger">
          <summary class="cursor-pointer">✗ {{ result.errors.length }} 条错误（展开）</summary>
          <ul class="ml-4 mt-1 max-h-32 list-disc overflow-y-auto text-xs">
            <li v-for="(err, i) in result.errors" :key="i">{{ err }}</li>
          </ul>
        </details>
      </div>

      <div class="mt-4 flex gap-2">
        <button
          class="btn btn-primary flex-1"
          :disabled="mutation.isPending.value"
          @click="startImport"
        >
          开始导入
        </button>
        <button class="btn btn-ghost flex-1" @click="emit('close')">关闭</button>
      </div>
    </div>
  </div>
</template>
